import React, { useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { Input } from "@/components/ui/input";
import { useAuth, formatApiError } from "@/lib/auth";
import { toast } from "sonner";
import Logo from "@/components/Logo";
import { ArrowLeft, ArrowUpRight } from "lucide-react";

const ROLES = [
  { value: "individual", label: "Individual", help: "I want to resolve a personal dispute." },
  { value: "business", label: "Business", help: "I file disputes for an organisation." },
  { value: "neutral", label: "Mediator / Arbitrator", help: "I'm a mediator or arbitrator." },
];

export default function Register() {
  const [role, setRole] = useState("individual");
  const [form, setForm] = useState({ name: "", email: "", password: "", phone: "", org_name: "" });
  const [agree, setAgree] = useState(false);
  const [loading, setLoading] = useState(false);
  const [err, setErr] = useState("");
  const { register } = useAuth();
  const nav = useNavigate();
  const set = (k, v) => setForm((f) => ({ ...f, [k]: v }));

  const submit = async (e) => {
    e.preventDefault(); setErr("");
    if (role === "business" && !form.org_name) {
      setErr("Organisation name is required for business accounts."); return;
    }
    if (!agree) {
      setErr("Please agree to the Terms of Service, Privacy Policy and User Agreement to continue.");
      return;
    }
    setLoading(true);
    try {
      await register({ ...form, role });
      toast.success("Account created.");
      nav("/app", { replace: true });
    } catch (e) {
      setErr(formatApiError(e.response?.data?.detail) || e.message);
    } finally { setLoading(false); }
  };

  return (
    <div className="min-h-screen bg-sand grid lg:grid-cols-2">
      <div className="hidden lg:flex bg-court text-sand p-12 flex-col">
        <Link to="/"><Logo tone="sand" /></Link>
        <div className="mt-auto">
          <div className="eyebrow-light mb-6">Begin</div>
          <h2 className="font-display text-[44px]" style={{ fontWeight: 400, lineHeight: 1.04 }}>
            Justice, made<br />collaborative.
          </h2>
          <p className="mt-6 text-sand/75 max-w-md text-[15px]">Tell us who you are. We'll route you to the right workspace.</p>
        </div>
      </div>
      <div className="flex items-center justify-center p-6 lg:p-12">
        <div className="w-full max-w-md">
          <Link to="/" className="inline-flex items-center text-[13px] text-ink/65 hover:text-ink mb-8"><ArrowLeft className="h-4 w-4 mr-1" />Back</Link>
          <div className="eyebrow">Create account</div>
          <h1 className="font-display text-[40px] text-court mt-3" style={{ fontWeight: 400, lineHeight: 1.05 }}>Who are you?</h1>

          <div className="grid grid-cols-3 mt-8 border hairline">
            {ROLES.map((r) => (
              <button
                key={r.value} data-testid={`role-${r.value}`} type="button"
                onClick={() => setRole(r.value)}
                className={`px-3 py-4 text-left border-r last:border-r-0 hairline transition-colors ${role === r.value ? "bg-court text-sand" : "bg-transparent text-ink hover:bg-stone/20"}`}
              >
                <div className={`font-mono text-[10px] tracking-[0.16em] uppercase ${role === r.value ? "text-clay" : "text-stone"}`}>0{ROLES.indexOf(r) + 1}</div>
                <div className="font-display text-[18px] mt-1" style={{ fontWeight: 400 }}>{r.label}</div>
              </button>
            ))}
          </div>
          <p className="text-[12px] text-ink/60 mt-3">{ROLES.find(r => r.value === role)?.help}</p>

          <form onSubmit={submit} className="mt-8 space-y-6" data-testid="register-form">
            <Field id="rn" testid="register-name" label="Full name" value={form.name} onChange={(v) => set("name", v)} />
            {role === "business" && <Field id="ro" testid="register-org" label="Organisation" value={form.org_name} onChange={(v) => set("org_name", v)} />}
            <Field id="re" testid="register-email" label="Email" type="email" value={form.email} onChange={(v) => set("email", v)} />
            <Field id="rp" testid="register-phone" label="Phone" value={form.phone} onChange={(v) => set("phone", v)} />
            <Field id="rw" testid="register-password" label="Password (min. 6)" type="password" value={form.password} onChange={(v) => set("password", v)} />

            <label className="flex gap-3 items-start cursor-pointer select-none" data-testid="register-agree-label">
              <input
                type="checkbox"
                checked={agree}
                onChange={(e) => setAgree(e.target.checked)}
                data-testid="register-agree"
                className="mt-1 h-4 w-4 accent-clay shrink-0"
              />
              <span className="text-[12px] text-ink/75 leading-snug">
                I have read and agree to the{" "}
                <Link to="/terms" target="_blank" className="text-clay underline-offset-2 hover:underline">Terms of Service</Link>,{" "}
                <Link to="/privacy" target="_blank" className="text-clay underline-offset-2 hover:underline">Privacy Policy</Link> and{" "}
                <Link to="/user-agreement" target="_blank" className="text-clay underline-offset-2 hover:underline">User Agreement</Link>. I confirm I am 18+ and legally capable of entering into a binding contract under the Indian Contract Act, 1872.
              </span>
            </label>

            {err && <div data-testid="register-error" className="text-[13px] text-clay border-l-2 border-clay pl-3">{err}</div>}
            <button type="submit" disabled={loading || !agree} data-testid="register-submit" className="inline-flex items-center gap-2 px-5 py-3 bg-clay text-sand hover:bg-[hsl(17_64%_42%)] text-sm w-full justify-center disabled:opacity-50 disabled:cursor-not-allowed" style={{ borderRadius: 3 }}>
              {loading ? "Creating…" : "Create account"} <ArrowUpRight className="h-4 w-4" />
            </button>
          </form>
          <div className="mt-8 text-[13px] text-ink/65">
            Already have an account? <Link to="/login" className="text-clay underline-offset-4 hover:underline" data-testid="register-to-login">Sign in</Link>
          </div>
        </div>
      </div>
    </div>
  );
}

function Field({ id, testid, label, type = "text", value, onChange }) {
  return (
    <div>
      <label htmlFor={id} className="eyebrow block mb-2">{label}</label>
      <Input id={id} data-testid={testid} type={type} required={type !== "tel"} value={value} onChange={(e) => onChange(e.target.value)}
        className="h-12 rounded-none bg-transparent border-0 border-b border-ink/25 focus:border-clay text-ink placeholder:text-ink/40 px-0" />
    </div>
  );
}
